import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

export interface LocationSuggestion {
  placeId: string;
  description: string;
}

export interface GeocodeResult {
  placeId?: string | null;
  formattedAddress: string;
  addressLine1?: string | null;
  latitude: number;
  longitude: number;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  zipCode?: string | null;
  area?: string | null;
}

interface PlaceSuggestionResponse {
  placeId: string;
  description: string;
}

type PlaceDetailsResponse = GeocodeResult;

@Injectable({ providedIn: 'root' })
export class LocationService {
  private readonly api = inject(ApiService);

  autocomplete(input: string): Observable<LocationSuggestion[]> {
    return this.api
      .post<ApiResponse<PlaceSuggestionResponse[]> | PlaceSuggestionResponse[], { input: string }>(
        API_ENDPOINTS.locations.autocomplete,
        { input },
        'marketplace',
        environment.mapsApiTimeoutMs,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((suggestions) =>
          suggestions.map((suggestion) => ({
            placeId: suggestion.placeId,
            description: suggestion.description,
          })),
        ),
      );
  }

  placeDetails(placeId: string): Observable<GeocodeResult> {
    return this.api
      .post<ApiResponse<PlaceDetailsResponse> | PlaceDetailsResponse, { placeId: string }>(
        API_ENDPOINTS.locations.placeDetails,
        { placeId },
        'marketplace',
        environment.mapsApiTimeoutMs,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((location) => ({
          ...location,
          latitude: Number(location.latitude),
          longitude: Number(location.longitude),
        })),
      );
  }

  reverseGeocode(latitude: number, longitude: number): Observable<GeocodeResult> {
    return this.api
      .get<ApiResponse<GeocodeResult> | GeocodeResult>(
        API_ENDPOINTS.locations.reverseGeocode,
        { latitude, longitude },
        'marketplace',
        environment.mapsApiTimeoutMs,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }
}
